<template>
    <v-form ref="form" v-model="valid" @submit="sendNotification">
        <v-container fluid class="px-1">
            <v-row>
                <v-col class="pt-1 pb-0">
                    <v-text-field
                            v-model="notification.Title"
                            :rules="[$validations.required]">
                        <template v-slot:label>
                            {{ $t('notifications.title') }}<sup>*</sup>
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="pt-1 pb-0">
                    <v-text-field
                            v-model="notification.Text"
                            :rules="[$validations.required]">
                        <template v-slot:label>
                            {{ $t('notifications.message') }}<sup>*</sup>
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="pt-1 pb-0">
                    <v-select
                            v-model="notification.Severity"
                            :items="severities"
                            :rules="[$validations.required]">
                        <template v-slot:label>
                            {{ $t('notifications.severity') }}<sup>*</sup>
                        </template>
                    </v-select>
                </v-col>
            </v-row>
            <v-row v-if="sendOnlyZone">
                <v-col class="d-flex align-center">
                    <div class="mr-3 body-1">{{ $t('notifications.sendNotificationUsers') }}:</div>
                    <v-radio-group
                            v-model="sendNotificationOutsideZone"
                            label=""
                            row
                    >
                        <v-radio
                                :label="$t('notifications.insideZone')"
                                :value="false"
                        ></v-radio>
                        <v-radio
                                :label="$t('notifications.outsideZone')"
                                :value="true"
                        ></v-radio>
                    </v-radio-group>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="pt-1 pb-0" cols="2">
                    <v-select
                            v-model="group"
                            :items="groups"
                            :disabled="sendOnlyZone"
                            name="category">
                    </v-select>
                </v-col>
                <v-col class="pt-1 pb-0" cols="10">
                    <v-autocomplete
                            v-model="selectedItems"
                            :items="selectableItems"
                            :rules="[$validations.required]"
                            :disabled="sendOnlyZone"
                            auto-select-first
                            chips
                            clearable
                            deletable-chips
                            class="mt-2"
                            item-text="Name"
                            multiple
                            return-object
                            small-chips
                    >
                        <template v-slot:label>
                            {{ $t('notifications.to') }}<sup>*</sup>
                        </template>
                    </v-autocomplete>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="text-right">
                    <v-btn
                            :disabled="!valid || selectedItems.length === 0"
                            :loading="loading"
                            class=" py-6 shadow-inverse accentText--text"
                            color="accent"
                            depressed
                            @click="sendNotification">
                        {{ $t('notifications.sendNotification') }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-container>
    </v-form>
</template>

<script>
import {
  NOTIFICATIONS_SEVERITY_CRITICAL,
  NOTIFICATIONS_SEVERITY_INFO,
  NOTIFICATIONS_SEVERITY_WARNING,
  USER_ACTIVE,
  ZONE_USER_MONITORED
} from "@/helper/constants";

export default {
  name: "NotificationSender",
  props: {
    circleID: {
      type: [String, Number],
      required: false,
      default: null
    },
    userID: {
      type: [String, Number],
      required: false,
      default: null
    },
    zoneID: {
      type: [String, Number],
      required: false,
      default: null
    }
  },
  created() {
    if (this.zoneID) {
      this.sendOnlyZone = true;
    }
    this.initUsers();
    this.initCircles();
    this.initZones();
    window.addEventListener('keyup', this.handleKeyup);
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.handleKeyup);
  },
  data() {
    return {
      notification: {
        Title: '',
        Text: '',
        Severity: '',
        UsersID: [],
        CirclesID: [],
      },
      selectedItems: [],
      circles: [],
      zones: [],
      users: [],
      group: 'users',
      valid: false,
      loading: false,
      sendNotificationOutsideZone: false,
      zoneOutsideUsersID: [],
      sendOnlyZone: false,
    };
  },
  computed: {
    severities() {
      return [
        {
          text: this.$t('notifications.info'),
          value: NOTIFICATIONS_SEVERITY_INFO
        },
        {
          text: this.$t('notifications.warning'),
          value: NOTIFICATIONS_SEVERITY_WARNING
        },
        {
          text: this.$t('notifications.critical'),
          value: NOTIFICATIONS_SEVERITY_CRITICAL
        }
      ];
    },
    groups() {
      return [
        {
          text: this.$t('notifications.users'),
          value: 'users'
        },
        {
          text: this.$t('menu.circles'),
          value: 'circles'
        },
        {
          text: this.$t('menu.zones'),
          value: 'zones'
        },
        {
          text: this.$t('notifications.all'),
          value: 'all'
        },
      ];
    },
    organizationID() {
      return this.$store.getters.getOrganizationId;
    },
    selectableItems() {
      const users = this.users.map((user) => ({
        ...user,
        Name: user.FirstName + ' ' + user.LastName + ' (' + user.Email + ')'
      }));
      if (this.group === 'users') {
        const selectedElements = this.selectedItems.filter((item) => !item.UserID);
        return [...users, ...selectedElements];
      }

      if (this.group === 'circles') {
        const selectedElements = this.selectedItems.filter((item) => !item.CircleID);
        return [...this.circles, ...selectedElements];
      }

      if (this.group === 'zones') {
        const selectedElements = this.selectedItems.filter((item) => !item.ZoneID);
        return [...this.zones, ...selectedElements];
      }
      return [...this.circles, ...this.zones, ...users];
    }
  },
  methods: {
    sendNotification() {
      this.$refs.form.validate();
      if (!this.valid || this.loading) {
        return;
      }
      if (this.sendOnlyZone && this.sendNotificationOutsideZone) {
        this.notification.UsersID = this.zoneOutsideUsersID;
        this.notification.CirclesID = [];
        this.notification.ZonesID = [];
      } else {
        this.notification.UsersID = this.selectedItems.filter((item) => item.UserID).map(item => item.UserID);
        this.notification.CirclesID = this.selectedItems.filter((item) => item.CircleID).map(item => item.CircleID);
        this.notification.ZonesID = this.selectedItems.filter((item) => item.ZoneID).map(item => item.ZoneID);
      }

      /*   if(this.notification.UsersID.length === 0) {
           delete this.notification.UsersID;
         }
         if(this.notification.CirclesID.length === 0) {
           delete this.notification.CirclesID;
         }*/
      const url = this.$store.getters.notifications;
      this.loading = true;
      this.axios.post(url, this.notification).then(() => {
        this.$refs.form.inputs.forEach(input => {
          if (!input.$attrs.name) {
            input.reset();
          }
        });
        this.group = 'users';
        this.$refs.form.resetErrorBag();
        this.sendOnlyZone = false;
        this.zoneOutsideUsersID = [];
        this.$store.dispatch('addNotification', {
          type: 'success',
          message: this.$t('success.notificationSent')
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    initUsers() {
      const url = this.$store.getters.getOrganizationUsers(this.organizationID);
      this.axios.get(url).then(({data}) => {
        this.users = data.Result.filter((user) => user.UserID !== this.$store.getters.getUserId && user.Status === USER_ACTIVE);
        if (this.userID) {
          const users = this.users.map((user) => ({
            ...user,
            Name: user.FirstName + ' ' + user.LastName + ' (' + user.Email + ')'
          }));
          this.selectedItems = users.filter((user) => user.UserID === parseInt(this.userID));
        }
      });
    },
    initCircles() {
      const url = this.$store.getters.circles(this.organizationID);
      this.axios.get(url).then(({data}) => {
        this.circles = data.Result;
        if (this.circleID) {
          this.group = 'circles';
          this.selectedItems = this.circles.filter((circle) => circle.CircleID === parseInt(this.circleID));
        }
      });
    },
    initZones() {
      const url = this.$store.getters.zones;
      this.axios.get(url).then(({data}) => {
        this.zones = data.Result;
        if (this.zoneID) {
          this.group = 'zones';
          this.selectedItems = this.zones.filter((zone) => zone.ZoneID === parseInt(this.zoneID));
          const url = this.$store.getters.zoneUsers(this.zoneID);
          this.axios.get(url).then(({data}) => {
            this.zoneOutsideUsersID = data.Result.filter((user) => user.Role === ZONE_USER_MONITORED && !user.IsInsideZone).map((user) => user.UserID);
          })
        }
      });
    },
    handleKeyup(e) {
      if (e.key === 'Enter') {
        if (this.valid && this.selectedItems.length) {
          this.sendNotification();
        }
      }
    },
  }
};
</script>

<style scoped>

</style>